import React, { useMemo } from "react";
import { Dialog, DialogTitle, DialogContent } from "@mui/material";
import type { TPort, TWeather } from "@/types";

interface PortWeatherForecastProps {
  port: TPort | null;
  weather: TWeather[] | undefined;
  open: boolean;
  onClose: () => void;
}

interface DailyRow {
  date: string;
  precipitation: number | null;
  swellHeight: number | null;
  visibility: number | null;
  windSpeed: number | null;
}

function extractContributorValue(
  val: any,
  contributor = "noaa"
): number | null {
  if (val == null) return null;
  if (typeof val === "number") return isFinite(val) ? val : null;
  if (typeof val === "string") {
    const p = parseFloat(val);
    return isFinite(p) ? p : null;
  }
  if (typeof val === "object") {
    if (contributor in val)
      return extractContributorValue(val[contributor], contributor);
    for (const k of Object.keys(val)) {
      const num = extractContributorValue(val[k], contributor);
      if (num != null) return num;
    }
  }
  if (Array.isArray(val)) {
    for (const v of val) {
      const num = extractContributorValue(v, contributor);
      if (num != null) return num;
    }
  }
  return null;
}

function formatMetric(val: number | null) {
  return val == null ? "-" : val.toFixed(2);
}

const PortWeatherForecast: React.FC<PortWeatherForecastProps> = ({
  port,
  weather,
  open,
  onClose,
}) => {
  const daily: DailyRow[] = useMemo(() => {
    if (!weather || weather.length === 0) return [];
    const byDay = new Map<string, any>();
    for (const obs of weather) {
      const dateStr = new Date((obs as any).time).toISOString().slice(0, 10);
      if (!byDay.has(dateStr)) byDay.set(dateStr, obs);
      if (byDay.size >= 10) break;
    }
    return Array.from(byDay.entries())
      .sort((a, b) => (a[0] < b[0] ? -1 : 1))
      .slice(0, 10)
      .map(([date, obs]) => ({
        date,
        precipitation: extractContributorValue(
          (obs as any).precipitation,
          "noaa"
        ),
        swellHeight: extractContributorValue((obs as any).swellHeight, "noaa"),
        visibility: extractContributorValue((obs as any).visibility, "noaa"),
        windSpeed: extractContributorValue((obs as any).windSpeed, "noaa"),
      }));
  }, [weather]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {port
          ? `${port.name} (${(port.unlocode || "").slice(0, 2).toUpperCase()})`
          : ""}
        <br />
        <span style={{ fontSize: 16 }}>
          10-Day Weather Forecast (from today)
        </span>
      </DialogTitle>
      <DialogContent>
        {weather === undefined && port && (
          <div style={{ fontSize: 12 }}>Loading forecast…</div>
        )}
        {weather !== undefined && daily.length === 0 && (
          <div style={{ fontSize: 12 }}>No forecast data available.</div>
        )}
        {daily.length > 0 && (
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontFamily: "Inter, sans-serif",
              fontSize: 12,
            }}
          >
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: 4 }}>Date</th>
                <th style={{ textAlign: "right", padding: 4 }}>
                  Precip (mm/h)
                </th>
                <th style={{ textAlign: "right", padding: 4 }}>Swell (m)</th>
                <th style={{ textAlign: "right", padding: 4 }}>Vis (km)</th>
                <th style={{ textAlign: "right", padding: 4 }}>Wind (m/s)</th>
              </tr>
            </thead>
            <tbody>
              {daily.map((d) => (
                <tr key={d.date}>
                  <td style={{ padding: 4 }}>{d.date}</td>
                  <td style={{ padding: 4, textAlign: "right" }}>
                    {formatMetric(d.precipitation)}
                  </td>
                  <td style={{ padding: 4, textAlign: "right" }}>
                    {formatMetric(d.swellHeight)}
                  </td>
                  <td style={{ padding: 4, textAlign: "right" }}>
                    {formatMetric(d.visibility)}
                  </td>
                  <td style={{ padding: 4, textAlign: "right" }}>
                    {formatMetric(d.windSpeed)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ marginTop: 12, fontSize: 11, opacity: 0.7 }}>
          Showing first available observation for each day up to 10 days from
          today. Wind speed at 10m above sea level.
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PortWeatherForecast;
